import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../utils/axios.ts';

export interface LoanDisbursement {
  id: number;
  loan_id: number;
  member_id: number;
  group_id: number;
  disbursed_by: number;
  disbursement_id?: number;
  amount_disbursed: number;
  currency: string;
  disbursed_at: string;
  disbursement_notes?: string;
  transaction_reference?: string;
  mpesa_transaction_id?: string;
  failure_reason?: string;
  created_at: string;
  updated_at: string;
  loan?: {
    id: number;
    loan_no: string;
    principal: number;
    total_amount: number;
    status: string;
  };
  member?: {
    id: number;
    name: string;
    phone_number: string;
  };
  disbursedBy?: {
    id: number;
    name: string;
    email: string;
  };
}

export interface CreateLoanDisbursementData {
  loan_id: number;
  amount_disbursed: number;
  disbursed_at: string;
  disbursement_notes?: string;
  transaction_reference?: string;
  currency?: string;
}

export interface UpdateLoanDisbursementData {
  disbursement_notes?: string;
  transaction_reference?: string;
  mpesa_transaction_id?: string;
  failure_reason?: string;
}

export interface LoanDisbursementFilters {
  loan_id?: number;
  member_id?: number;
  date_from?: string;
  date_to?: string;
  search?: string;
  page?: number;
  per_page?: number;
}

export interface LoanDisbursementStatistics {
  total_disbursements: number;
  total_amount: number;
  completed_disbursements: number;
  pending_disbursements: number;
  failed_disbursements: number;
  average_amount: number;
}

// Fetch loan disbursements with filters
export const useLoanDisbursements = (filters: LoanDisbursementFilters = {}) => {
  return useQuery({
    queryKey: ['loan-disbursements', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });

      const response = await api.get(`/loan-disbursements?${params.toString()}`);
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Fetch single loan disbursement
export const useLoanDisbursement = (id: number) => {
  return useQuery({
    queryKey: ['loan-disbursement', id],
    queryFn: async () => {
      const response = await api.get(`/loan-disbursements/${id}`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Create loan disbursement
export const useCreateLoanDisbursement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateLoanDisbursementData) => {
      const response = await api.post('/loan-disbursements', data);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate and refetch loan disbursements
      queryClient.invalidateQueries({ queryKey: ['loan-disbursements'] });
      queryClient.invalidateQueries({ queryKey: ['loans'] });
      queryClient.invalidateQueries({ queryKey: ['loan-disbursement-statistics'] });
    },
  });
};

// Update loan disbursement
export const useUpdateLoanDisbursement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdateLoanDisbursementData }) => {
      const response = await api.put(`/loan-disbursements/${id}`, data);
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate and refetch specific disbursement and list
      queryClient.invalidateQueries({ queryKey: ['loan-disbursement', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['loan-disbursements'] });
      queryClient.invalidateQueries({ queryKey: ['loan-disbursement-statistics'] });
    },
  });
};

// Delete loan disbursement
export const useDeleteLoanDisbursement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await api.delete(`/loan-disbursements/${id}`);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate and refetch loan disbursements
      queryClient.invalidateQueries({ queryKey: ['loan-disbursements'] });
      queryClient.invalidateQueries({ queryKey: ['loan-disbursement-statistics'] });
    },
  });
};

// Fetch loan disbursement statistics
export const useLoanDisbursementStatistics = (filters: { date_from?: string; date_to?: string } = {}) => {
  return useQuery({
    queryKey: ['loan-disbursement-statistics', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });

      const response = await api.get(`/loan-disbursements/statistics/summary?${params.toString()}`);
      return response.data;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Get disbursements for a specific loan
export const useLoanDisbursementsByLoan = (loanId: number) => {
  return useQuery({
    queryKey: ['loan-disbursements-by-loan', loanId],
    queryFn: async () => {
      const response = await api.get(`/loan-disbursements?loan_id=${loanId}`);
      return response.data;
    },
    enabled: !!loanId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Get disbursements for a specific member
export const useLoanDisbursementsByMember = (memberId: number) => {
  return useQuery({
    queryKey: ['loan-disbursements-by-member', memberId],
    queryFn: async () => {
      const response = await api.get(`/loan-disbursements?member_id=${memberId}`);
      return response.data;
    },
    enabled: !!memberId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};
